import { useState, useMemo, useCallback, useEffect } from 'react';
import type { Meta, StoryFn } from '@storybook/react';

import type DateTimeProps from '@pega/cosmos-react-core/lib/components/DateTime/DateTime.types';
import type { DateTimeCallbackParameter } from '@pega/cosmos-react-core/lib/components/DateTime/DateTime.types';
import { BuildTheme, ThemeOverride, Flex, FormDialog, Input } from '@pega/cosmos-react-core';
import { CodeEditor, DynamicInput } from '@pega/cosmos-react-build';
import type {
  RuleSelectProps,
  SourceConfig,
  ConstantControlConfig,
  Source,
  RuleSelectItem
} from '@pega/cosmos-react-build';

import { useFieldReferenceMock } from '../FieldReference/FieldReference.mocks';

import { baseRuleTypes, defaultRuleState, fetchRules } from './DynamicInput.mocks';

export default {
  title: 'Build/DynamicInput',
  component: DynamicInput,
  args: {
    constantFormat: 'Text',
    defaultSource: 'Rule',
    required: true
  },
  argTypes: {
    constantFormat: {
      control: { type: 'select' },
      options: [
        'Text',
        'Text (paragraph)',
        'Boolean',
        'Currency',
        'Percentage',
        'Integer',
        'Decimal',
        'Phone',
        'DateTime',
        'Date',
        'Time'
      ]
    },
    defaultSource: {
      control: { type: 'select' },
      options: ['Rule', 'Constant', 'FieldReference', 'Expression']
    },
    required: {
      control: { type: 'boolean' }
    }
  }
} as Meta;

type RuleType = 'Automation' | 'Decision' | 'Field' | 'Paragraph';

interface DynamicInputStoryProps {
  constantFormat?: ConstantControlConfig['format'];
  required?: boolean;
  defaultSource?: Source;
  timeout?: boolean;
}

export const DynamicInputDemo: StoryFn<DynamicInputStoryProps> = (args: DynamicInputStoryProps) => {
  const defaultSource = args.defaultSource || 'Rule';
  const constantFormat = args.constantFormat || 'Text';
  const timeout = args.timeout || true;

  const [sources, setSources] = useState<SourceConfig[]>([
    {
      type: 'Constant',
      selected: defaultSource === 'Constant'
    },
    {
      type: 'Rule',
      selected: defaultSource === 'Rule'
    },
    {
      type: 'FieldReference',
      selected: defaultSource === 'FieldReference'
    },
    {
      type: 'Expression',
      selected: defaultSource === 'Expression'
    }
  ]);

  useEffect(() => {
    setSources([
      {
        type: 'Constant',
        selected: defaultSource === 'Constant'
      },
      {
        type: 'Rule',
        selected: defaultSource === 'Rule'
      },
      {
        type: 'FieldReference',
        selected: defaultSource === 'FieldReference'
      },
      {
        type: 'Expression',
        selected: defaultSource === 'Expression'
      }
    ]);
  }, [defaultSource]);

  /**
   * Constant Input
   */
  const [constantValue, setConstantValue] = useState<ConstantControlConfig['props']['value']>('');
  const constant = useMemo<ConstantControlConfig>(() => {
    if (constantFormat === 'Boolean') {
      return {
        format: 'Boolean',
        props: {
          value: constantValue === true,
          onChange: value => {
            setConstantValue(value);
          }
        }
      };
    }
    if (constantFormat === 'Date' || constantFormat === 'DateTime' || constantFormat === 'Time') {
      return {
        format: constantFormat,
        props: {
          value: constantValue as DateTimeProps['value'],
          onChange: (value: DateTimeCallbackParameter) => {
            setConstantValue(value.valueAsISOString);
          }
        }
      };
    }
    return {
      format: constantFormat,
      props: {
        value: String(constantValue),
        onChange: (value: ConstantControlConfig['props']['value']) => {
          setConstantValue(value);
        }
      }
    };
  }, [constantValue, constantFormat]);

  /**
   * Rule input
   */
  const [ruleState, setRuleState] = useState<{
    props: typeof defaultRuleState;
    pendingFetch?: ReturnType<typeof fetchRules>;
  }>({
    props: defaultRuleState
  });
  const [ruleConfigDialogTarget, setRuleConfigDialogTarget] = useState<HTMLElement>();

  const onRuleExpand = useCallback(
    (itemId: string) => {
      const expandedItem = ruleState.props.ruleTypes?.find(item => item.id === itemId);

      // Demos lazy loading rules.
      if (expandedItem?.rules.length === 0) {
        const { promise } = fetchRules({ ruleType: expandedItem.type, delay: timeout ? 1000 : 0 });

        promise
          .then(res => {
            const newItems = ruleState.props.ruleTypes?.map(item => {
              if (item.id === expandedItem.id) {
                return { ...item, rules: res.results };
              }

              return item;
            });

            setRuleState({
              ...ruleState,
              props: { ...ruleState.props, loading: false, ruleTypes: newItems }
            });
          })
          .catch(() => {});

        // Set loading while rules are fetched initially.
        setRuleState({
          ...ruleState,
          props: { ...ruleState.props, loading: true }
        });
      }
    },
    [ruleState]
  );

  const onRuleFilter = useCallback(
    (filterValue: string) => {
      // Abort requests before reissuing if necessary.
      if (ruleState.pendingFetch) {
        ruleState.pendingFetch.abort();
      }

      // If the filterValue is an empty string from backspace both remove any selection and reset the menu to its default.
      if (!filterValue) {
        setRuleState({
          ...ruleState,
          props: {
            ...defaultRuleState
          }
        });
        return;
      }

      const fetch = fetchRules({ filter: filterValue, delay: timeout ? 1000 : 0 });

      fetch.promise.then(fetchedRules => {
        if (!ruleState.props) {
          return undefined;
        }

        setRuleState({
          pendingFetch: undefined,
          props: {
            ...ruleState.props,
            filterValue,
            loading: false,
            items: fetchedRules.results
          }
        });
      });

      // Place the menu into a loading state while rules are fetched.
      // Additionally, set a reference to the pending fetch to conditionally abort if onFilter is retriggered before fetch resolves.
      setRuleState({
        pendingFetch: fetch,
        props: {
          ...ruleState.props,
          loading: true,
          filterValue,
          ruleTypes: [],
          items: undefined
        }
      });
    },
    [ruleState]
  );

  const ruleLoadMore = useCallback(
    (itemId: string | undefined) => {
      const ruleStateProps = ruleState.props as RuleSelectProps;
      const ruleItems = ruleStateProps.items;

      const directList = !!ruleState.props.items;

      // No rule type expanded and no items provided directly.
      if (!itemId && !directList) {
        return ruleStateProps;
      }

      const expandedItem = ruleState.props.ruleTypes?.find(item => item.id === itemId);

      // Apply based on if an expanded rule or a list of items provided directly.
      let fetchParams = {};

      // Expanded rule type's list.
      if (!directList && expandedItem) {
        const currentLength = expandedItem?.rules.length;

        // The demo is assuming a total count in known upfront.
        // (If that is not the case an empty response can be used to determine all rules are fetched)
        const totalCount = baseRuleTypes.find(item => item.id === itemId)!.count!;

        if (currentLength >= totalCount) {
          return ruleStateProps;
        }

        fetchParams = {
          ruleType: itemId as RuleType,
          start: currentLength,
          delay: timeout ? 1000 : 0
        };
      }

      // Abort requests before reissuing if necessary.
      if (ruleState.pendingFetch) {
        ruleState.pendingFetch.abort();
      }

      const fetch = fetchRules(fetchParams);

      fetch.promise.then(res => {
        setRuleState({
          pendingFetch: undefined,
          props: {
            ...ruleState.props,
            loading: false,
            items: ruleItems
              ? {
                  ...ruleItems,
                  ...res.results
                }
              : undefined,
            ruleTypes: expandedItem
              ? ruleState.props.ruleTypes?.map(item => {
                  if (item.id === expandedItem.id) {
                    return {
                      ...item,
                      rules: [...item.rules, ...res.results]
                    };
                  }

                  return item;
                })
              : defaultRuleState.ruleTypes
          }
        });
      });

      setRuleState({
        pendingFetch: fetch,
        props: {
          ...ruleState.props,
          loading: true
        }
      });
    },
    [ruleState]
  );

  const onRuleSelect = useCallback(
    (selectedRule: RuleSelectItem) => {
      setRuleState({
        ...ruleState,
        props: {
          ...ruleState.props,
          filterValue: undefined,
          selectedRule: {
            ...selectedRule,
            href: `/rules/${selectedRule.id}`,
            onPreview: () => {
              // eslint-disable-next-line no-alert
              alert('Link previewed');
            },
            onLinkClick: () => {
              // eslint-disable-next-line no-alert
              alert('Link clicked');
            },
            onConfig: configTarget => {
              setRuleConfigDialogTarget(configTarget);
            },
            onClear: () => {
              setRuleConfigDialogTarget(undefined);
              setRuleState({
                props: defaultRuleState
              });
            }
          }
        }
      });
    },
    [ruleState]
  );

  /**
   * Field reference input
   */
  const fieldReferenceMock = useFieldReferenceMock('static');

  return (
    <ThemeOverride theme={BuildTheme}>
      <DynamicInput
        required={args.required}
        sources={sources}
        onSourceChange={selectedSource => {
          const updatedSources = sources.map(source => ({
            ...source,
            selected: source.type === selectedSource
          }));
          setSources(updatedSources);
        }}
        controls={{
          constant,
          rule: {
            props: {
              ...ruleState.props,
              onExpand: onRuleExpand,
              onFilter: onRuleFilter,
              loadMore: ruleLoadMore,
              onSelect: onRuleSelect,
              onCreateNew: () => {}
            }
          },
          fieldReference: {
            props: {
              placeholder: 'Choose a field...',
              onSelectedChange: fieldReferenceMock.handleSelectedChange,
              menu: {
                items: fieldReferenceMock.itemsToRender,
                altContexts: fieldReferenceMock.altContexts,
                currentContext: fieldReferenceMock.currentContext,
                parentItem: fieldReferenceMock.parentItem,
                loading: fieldReferenceMock.menuLoading
              },
              selected: fieldReferenceMock.selected,
              filterValue: fieldReferenceMock.filterValue,
              onFilterChange: fieldReferenceMock.setFilterValue,
              onBlur: () => {
                fieldReferenceMock.setFilterValue('');
              }
            }
          },
          expression: {
            component: CodeEditor,
            props: {
              list: {
                onItemAdd: () => {},
                details: {}
              },
              search: {
                filters: [],
                onFilterChange: () => {},
                onSearchChange: () => {},
                value: ''
              },
              codeEditorHandle: {
                current: {
                  getValue: () => {
                    return undefined;
                  },
                  insertText: (text: string) => {
                    // eslint-disable-next-line no-console
                    console.log(text);
                  }
                }
              }
            }
          }
        }}
      />

      {/* Mock demoing rule config by way of a dialog but, any pattern could be used: modal, panel, etc... */}
      {ruleConfigDialogTarget && ruleState && ruleState.props?.selectedRule && (
        <FormDialog
          target={ruleConfigDialogTarget}
          heading={ruleState.props?.selectedRule.name}
          onCancel={() => {
            setRuleConfigDialogTarget(undefined);
          }}
          onSubmit={() => {
            setRuleConfigDialogTarget(undefined);
          }}
        >
          <Flex container={{ direction: 'column', gap: 2 }}>
            <Input label='Config field 1' />
            <Input label='Config field 2' />
            <Input label='Config field 3' />
          </Flex>
        </FormDialog>
      )}
    </ThemeOverride>
  );
};
